import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { Check, Layers, Loader2 } from "lucide-react";
import { Button } from "@/components/ui/button";
import { AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle } from "@/components/ui/alert-dialog";
import { api, errMsg } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { useLang } from "@/i18n";
import { RenewButton, renewalPending } from "@/components/RenewalAlert";

const PlanStatus = ({ sub, date, busy, onCancel, onResume }) => {
  const { t } = useLang();
  const [open, setOpen] = useState(false);
  return (
    <div className="mt-8 space-y-3" data-testid="shop-plan-status">
      {renewalPending({ subscription: sub }) ? (
        <>
          <p className="rounded-lg bg-red-50 p-3 text-sm font-semibold text-red-700" data-testid="shop-plan-pending">{t("renew.title")}</p>
          <RenewButton className="h-11 w-full" testId="shop-renew-button" />
        </>
      ) : <p className="rounded-lg bg-emerald-50 p-3 text-sm font-semibold text-emerald-800">{t("pricing.active")}</p>}
      {!renewalPending({ subscription: sub }) && <p className="text-xs text-slate-500" data-testid="shop-plan-period">{sub.cancel_at_period_end ? t("pricing.endsOn", { d: date }) : t("pricing.renews", { d: date })}</p>}
      {sub.cancel_at_period_end ? (
        <Button onClick={onResume} disabled={busy} className="h-11 w-full rounded-full" data-testid="shop-resume-button">{t("pricing.resumePlan")}</Button>
      ) : (
        <Button variant="outline" onClick={() => setOpen(true)} disabled={busy} className="h-11 w-full rounded-full" data-testid="shop-cancel-button">{t("pricing.cancelPlan")}</Button>
      )}
      <AlertDialog open={open} onOpenChange={setOpen}>
        <AlertDialogContent data-testid="shop-cancel-dialog">
          <AlertDialogHeader>
            <AlertDialogTitle>{t("pricing.cancelTitle")}</AlertDialogTitle>
            <AlertDialogDescription>{t("pricing.cancelText", { d: date })}</AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel data-testid="shop-cancel-keep">{t("pricing.keep")}</AlertDialogCancel>
            <AlertDialogAction onClick={onCancel} className="bg-red-600 hover:bg-red-700" data-testid="shop-cancel-confirm">{t("pricing.confirmCancel")}</AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
};

export const ShopPlanCard = ({ usd }) => {
  const { user, refresh } = useAuth();
  const { t, tErr, href, lang, locale } = useLang();
  const navigate = useNavigate();
  const [busy, setBusy] = useState(false);
  const sub = user?.subscription;
  const subscribed = !!sub && ["active", "trialing", "past_due"].includes(sub.status);
  const date = sub?.current_period_end ? new Date(sub.current_period_end).toLocaleDateString(locale) : "—";

  const call = async (fn) => {
    setBusy(true);
    try { await fn(); } catch (e) { toast.error(tErr(errMsg(e))); } finally { setBusy(false); }
  };
  const subscribe = () => {
    if (!user) { navigate(`/entrar?next=${href("pricing")}`); return; }
    call(async () => {
      const { data } = await api.post("/payments/checkout", { plan: "shop", origin_url: window.location.origin, lang });
      window.location.href = data.checkout_url;
    });
  };
  const cancel = () => call(async () => { await api.post("/payments/subscription/cancel"); await refresh(); toast.success(t("pricing.cancelDone")); });
  const resume = () => call(async () => { await api.post("/payments/subscription/resume"); await refresh(); toast.success(t("pricing.resumeDone")); });

  return (
    <div className="relative rounded-2xl bg-slate-900 p-8 text-white shadow-xl" data-testid="shop-plan-card">
      <span className="absolute -top-3 left-8 flex items-center gap-1 rounded-full bg-[#FFB800] px-3 py-1 font-mono text-[10px] font-semibold uppercase tracking-wider text-slate-900"><Layers className="h-3 w-3" />{t("pricing.shopTag")}</span>
      <p className="font-mono text-xs uppercase tracking-wider text-slate-400">{t("pricing.shop")}</p>
      <p className="mt-4 font-display text-5xl font-bold" data-testid="shop-plan-price">{usd(19)}<span className="text-lg font-medium text-slate-400">{t("pricing.perMonth")}</span></p>
      <p className="mt-1 text-sm text-slate-400">{t("pricing.shopCredits", { per: usd(0.19) })}</p>
      <ul className="mt-8 space-y-3">{t("pricing.shopList").map((f) => <li key={f} className="flex gap-2 text-sm text-slate-200"><Check className="h-4 w-4 text-[#FFB800]" />{f}</li>)}</ul>
      {subscribed ? <PlanStatus sub={sub} date={date} busy={busy} onCancel={cancel} onResume={resume} /> : (
        <Button onClick={subscribe} disabled={busy} className="mt-8 h-12 w-full rounded-full bg-[#FFB800] text-base text-slate-900 hover:bg-[#e6a600]" data-testid="shop-subscribe-button">
          {busy && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}{t("pricing.subscribe")}
        </Button>
      )}
      <p className="mt-3 text-center text-xs text-slate-500">{t("pricing.secure")}</p>
    </div>
  );
};
